import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileSignature, Upload, Loader2, FileText, FileDown, RefreshCw, Trash2, Pencil, Check, Plus, Highlighter, Eye, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (path, download = false) =>
  `${API_BASE}/documents/file/${path}?auth=${encodeURIComponent(localStorage.getItem("cf_token") || "")}${download ? "&download=1" : ""}`;
const today = () => new Date().toLocaleDateString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\./g, "-");

function UploadBox({ onUploaded, compact = false }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!/\.docx$/i.test(file.name)) { toast.error("Vælg et Word-dokument (.docx)"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("name", file.name.replace(/\.docx$/i, ""));
      const r = await api.post("/contracts/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Skabelon gemt · ${r.data.fields.length} felter fundet`);
      onUploaded(r.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fejlede"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={pick} data-testid="contract-template-input" />
      {compact ? (
        <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy} className="rounded-lg gap-1.5" data-testid="contract-add-template-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Ny skabelon
        </Button>
      ) : (
        <button onClick={() => ref.current?.click()} disabled={busy} data-testid="contract-upload-box"
          className="w-full rounded-2xl border-2 border-dashed border-[#cbd5e1] bg-white hover:border-[#C8102E] hover:bg-red-50/30 transition-colors px-6 py-10 flex flex-col items-center gap-3 text-center">
          {busy ? <Loader2 className="w-8 h-8 animate-spin text-[#C8102E]" /> : <Upload className="w-8 h-8 text-[#C8102E]" />}
          <span className="font-semibold text-slate-900">Upload din kontrakt (Word .docx)</span>
          <span className="text-sm text-slate-500 max-w-md">Alt du har markeret med <mark className="bg-yellow-300 px-1 rounded">gul</mark> i Word bliver til felter, du udfylder hver gang.</span>
        </button>
      )}
    </>
  );
}

export function ContractBuilder() {
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({});
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [editLabels, setEditLabels] = useState(false);
  const [labelDraft, setLabelDraft] = useState({});
  const [preview, setPreview] = useState(null);
  const replaceRef = useRef(null);

  const tpl = useMemo(() => templates.find((t) => t.id === tplId) || null, [templates, tplId]);

  const loadRecent = async () => {
    try {
      const r = await api.get("/documents");
      setRecent(r.data.filter((d) => d.template_id).slice(0, 8));
    } catch { /* */ }
  };
  const load = async (selectId) => {
    setLoading(true);
    try {
      const r = await api.get("/contracts/templates");
      setTemplates(r.data);
      setTplId((cur) => selectId || (r.data.find((t) => t.id === cur) ? cur : r.data[0]?.id || ""));
    } catch { toast.error("Kunne ikke hente skabeloner"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); loadRecent(); }, []);
  useEffect(() => { setValues({}); setResult(null); setEditLabels(false); setTitle(tpl ? `${tpl.name} ${today()}` : ""); }, [tplId]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = (f) => tpl?.labels?.[f.key] || f.label;
  const filled = tpl ? tpl.fields.filter((f) => (values[f.key] || "").trim()).length : 0;
  const total = tpl?.fields.length || 0;

  const generate = async () => {
    if (filled < total) { toast.error(`Udfyld alle felter (${total - filled} mangler)`); return; }
    setBusy(true);
    try {
      const r = await api.post("/contracts/generate", { template_id: tpl.id, title, values });
      setResult(r.data); toast.success("Kontrakten er lavet og gemt i Dokumentarkivet"); loadRecent();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunne ikke lave kontrakten"); }
    finally { setBusy(false); }
  };

  const reset = () => { setValues({}); setResult(null); setTitle(`${tpl.name} ${today()}`); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const replaceFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !tpl) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/contracts/templates/${tpl.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Skabelon opdateret · ${r.data.fields.length} felter`); load(tpl.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fejlede"); }
  };

  const saveLabels = async () => {
    try {
      await api.put(`/contracts/templates/${tpl.id}`, { labels: labelDraft });
      toast.success("Feltnavne gemt"); setEditLabels(false); load(tpl.id);
    } catch { toast.error("Kunne ikke gemme"); }
  };

  const rename = async () => {
    const name = window.prompt("Navn på skabelonen", tpl.name);
    if (!name || !name.trim()) return;
    try { await api.put(`/contracts/templates/${tpl.id}`, { name }); load(tpl.id); } catch { toast.error("Kunne ikke gemme"); }
  };

  const removeTpl = async () => {
    if (!window.confirm(`Slet skabelonen "${tpl.name}"? Kontrakter du allerede har lavet bliver liggende i Dokumentarkivet.`)) return;
    try { await api.delete(`/contracts/templates/${tpl.id}`); toast.success("Skabelon slettet"); setTplId(""); load(); }
    catch { toast.error("Kunne ikke slette"); }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="contract-builder">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-light text-slate-900 flex items-center gap-2"><FileSignature className="w-6 h-6 text-[#C8102E]" /> Ansættelseskontrakt</h2>
          <p className="text-sm text-slate-500">Udfyld felterne, og hent kontrakten som Word og PDF. Den gemmes automatisk i Dokumentarkivet.</p>
        </div>
        {templates.length > 0 && <UploadBox compact onUploaded={(t) => load(t.id)} />}
      </div>

      {loading && <p className="text-sm text-slate-500 py-6">Henter…</p>}

      {!loading && templates.length === 0 && (
        <div className="space-y-4">
          <UploadBox onUploaded={(t) => load(t.id)} />
          <div className="rounded-2xl bg-white border border-[#e9edf1] p-5 text-sm text-slate-600 space-y-2" data-testid="contract-howto">
            <p className="font-semibold text-slate-900 flex items-center gap-2"><Highlighter className="w-4 h-4 text-yellow-500" /> Sådan markerer du felterne i Word</p>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Markér den tekst, der skal rettes hver gang, f.eks. <i>[Navn]</i>, <i>[Startdato]</i> eller <i>[Timeløn]</i>.</li>
              <li>Vælg <b>Tekstfremhævningsfarve</b> (overstregningstusch-ikonet) → <b>Gul</b>.</li>
              <li>Står det samme felt flere steder, så skriv det præcis ens. Så udfylder du det kun én gang.</li>
              <li>Gem som <b>.docx</b>, og upload filen her.</li>
            </ol>
          </div>
        </div>
      )}

      {!loading && tpl && (
        <>
          {templates.length > 1 && (
            <div className="flex flex-wrap gap-2" data-testid="contract-template-tabs">
              {templates.map((t) => (
                <button key={t.id} onClick={() => setTplId(t.id)} data-testid={`contract-tpl-${t.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${t.id === tplId ? "bg-[#C8102E] text-white" : "bg-white border border-[#e2e8f0] text-slate-600"}`}>{t.name}</button>
              ))}
            </div>
          )}

          <div className="rounded-2xl bg-white border border-[#e9edf1] p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="contract-template-card">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-sky-600" /></div>
              <div className="min-w-0">
                <p className="font-medium text-slate-900 truncate" data-testid="contract-template-name">{tpl.name}</p>
                <p className="text-xs text-slate-500 truncate">{tpl.filename} · {total} felter</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <input ref={replaceRef} type="file" accept=".docx" className="hidden" onChange={replaceFile} data-testid="contract-replace-input" />
              <Button variant="ghost" size="sm" className="h-8 text-slate-600" onClick={rename} data-testid="contract-rename-btn"><Pencil className="w-3.5 h-3.5 mr-1" /> Omdøb</Button>
              <a href={fileUrl(tpl.file_path, true)} className="inline-flex items-center h-8 px-3 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="contract-template-download"><FileDown className="w-3.5 h-3.5 mr-1" /> Hent skabelon</a>
              <Button variant="ghost" size="sm" className="h-8 text-slate-600" onClick={() => replaceRef.current?.click()} data-testid="contract-replace-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Udskift fil</Button>
              <Button variant="ghost" size="sm" className="h-8 text-slate-400 hover:text-red-600" onClick={removeTpl} data-testid="contract-delete-tpl-btn"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>

          {result ? (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 space-y-4" data-testid="contract-result">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold"><Check className="w-5 h-5" /> {result.title}</div>
              <p className="text-sm text-emerald-700">Kontrakten er gemt i Dokumentarkivet under Kontrakter.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <a href={fileUrl(result.docx_path, true)} className="inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-[#2B579A] text-white text-sm font-medium hover:opacity-90" data-testid="contract-download-docx"><FileDown className="w-4 h-4" /> Hent Word</a>
                <a href={fileUrl(result.file_path, true)} className="inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-[#C8102E] text-white text-sm font-medium hover:opacity-90" data-testid="contract-download-pdf"><FileDown className="w-4 h-4" /> Hent PDF</a>
                <Button variant="outline" onClick={() => setPreview(result)} className="h-10 rounded-lg gap-2" data-testid="contract-preview-btn"><Eye className="w-4 h-4" /> Se PDF</Button>
              </div>
              <p className="text-xs text-emerald-700/80">Word-filen har præcis samme opsætning som din skabelon og kan rettes bagefter. PDF'en er klar til at sende og underskrive.</p>
              <Button onClick={reset} variant="ghost" className="gap-2 text-emerald-800 hover:bg-emerald-100" data-testid="contract-new-btn"><RotateCcw className="w-4 h-4" /> Lav en ny kontrakt</Button>
            </div>
          ) : (
            <div className="rounded-2xl bg-white border border-[#e9edf1] p-5 space-y-4" data-testid="contract-form">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">Udfyld felterne <span className="text-slate-400 font-normal">({filled}/{total})</span></p>
                {editLabels ? (
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setEditLabels(false)} className="h-8">Annullér</Button>
                    <Button size="sm" onClick={saveLabels} className="h-8 bg-[#C8102E] hover:bg-[#a50d26] text-white" data-testid="contract-save-labels-btn">Gem feltnavne</Button>
                  </div>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => { setLabelDraft({ ...(tpl.labels || {}) }); setEditLabels(true); }} className="h-8 text-slate-500" data-testid="contract-edit-labels-btn"><Pencil className="w-3.5 h-3.5 mr-1" /> Ret feltnavne</Button>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500">Titel i Dokumentarkivet</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-[#f8fafc]" data-testid="contract-title-input" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {tpl.fields.map((f, i) => {
                  const long = f.key.length > 50;
                  return (
                    <div key={f.key} className={long ? "sm:col-span-2" : ""} data-testid={`contract-field-${i}`}>
                      {editLabels ? (
                        <Input value={labelDraft[f.key] ?? ""} placeholder={f.label} onChange={(e) => setLabelDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                          className="h-7 text-xs mb-1 border-dashed" data-testid={`contract-label-input-${i}`} />
                      ) : (
                        <label className="text-xs font-medium text-slate-600 flex items-center gap-1.5 flex-wrap">
                          <span className="truncate max-w-full">{label(f)}</span>
                          {f.count > 1 && <span className="text-[10px] px-1.5 rounded-full bg-sky-50 text-sky-700">{f.count} steder</span>}
                        </label>
                      )}
                      {long ? (
                        <Textarea value={values[f.key] || ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                          placeholder={f.label} rows={3} className="mt-1 bg-[#f8fafc]" data-testid={`contract-input-${i}`} />
                      ) : (
                        <Input value={values[f.key] || ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                          placeholder={f.label} className="mt-1 bg-[#f8fafc]" data-testid={`contract-input-${i}`} />
                      )}
                    </div>
                  );
                })}
              </div>

              <Button onClick={generate} disabled={busy} className="w-full h-11 rounded-lg bg-[#C8102E] hover:bg-[#a50d26] text-white gap-2" data-testid="contract-generate-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Lav kontrakt
              </Button>
            </div>
          )}

          {recent.length > 0 && (
            <div className="space-y-2" data-testid="contract-recent">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Seneste kontrakter</p>
              {recent.map((d) => (
                <div key={d.id} className="rounded-xl bg-white border border-[#e9edf1] px-4 py-2.5 flex items-center gap-3" data-testid={`contract-recent-${d.id}`}>
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-800 truncate">{d.title}</p>
                    <p className="text-[11px] text-slate-400">{d.created_at ? new Date(d.created_at).toLocaleDateString("da-DK") : ""}</p>
                  </div>
                  {d.docx_path && <a href={fileUrl(d.docx_path, true)} className="text-xs font-medium text-[#2B579A] hover:underline">Word</a>}
                  <a href={fileUrl(d.file_path, true)} className="text-xs font-medium text-[#C8102E] hover:underline">PDF</a>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-white max-w-3xl" data-testid="contract-preview-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light text-base">{preview?.filename}</DialogTitle></DialogHeader>
          {preview && <iframe src={fileUrl(preview.file_path)} title="PDF" className="w-full h-[70vh] rounded-lg bg-white" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ContractBuilder;
